import { useQuery } from '@tanstack/react-query'
import { lazy, Suspense, type ComponentProps } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { isNotAvailable } from '@/api/client'
import { EmptyState, PageHeader } from '@/components/page'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { toAmpel } from '@/features/deadlines/api'
import { kindLabel } from '@/features/deadlines/labels'
import { kpiQuery } from '@/features/dashboard/api'
import { AwaitingAcceptance } from '@/features/dashboard/AwaitingAcceptance'
import { AmpelBadge } from '@/features/orders/AmpelBadge'
import { requireNav } from '@/nav/guard'

// Recharts erst laden, wenn das Dashboard Daten zeigt
const LazyBarChartCard = lazy(() =>
  import('@/features/dashboard/BarChartCard').then((m) => ({ default: m.BarChartCard })),
)

function BarChartCard(props: ComponentProps<typeof LazyBarChartCard>) {
  return (
    <Suspense fallback={<Skeleton className="h-56 w-full" />}>
      <LazyBarChartCard {...props} />
    </Suspense>
  )
}

export const Route = createFileRoute('/_app/dashboard')({
  beforeLoad: requireNav('dashboard'),
  component: DashboardPage,
})

/** Lesezugang des Auftraggebers (S-01, S-04, S-07): Status, Durchlauf, nächste Fristen, Doku-Fortschritt. Keine Preise. */
function DashboardPage() {
  const { t, i18n } = useTranslation()
  const report = useQuery(kpiQuery)
  const formatDate = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    )
  const head = (
    <PageHeader title={t('pages.dashboard.title')} description={t('pages.dashboard.description')} />
  )

  if (report.isPending) {
    return (
      <>
        {head}
        <Skeleton className="h-64 w-full" aria-label={t('common.loading')} />
      </>
    )
  }
  if (report.isError) {
    return (
      <>
        {head}
        {isNotAvailable(report.error) ? (
          <EmptyState title={t('errors.notConnectedTitle')} body={t('errors.notConnectedBody')} />
        ) : (
          <EmptyState title={t('dashboard.loadError')} body={t('errors.network')}>
            <Button variant="outline" onClick={() => void report.refetch()}>
              {t('common.retry')}
            </Button>
          </EmptyState>
        )}
      </>
    )
  }

  const data = report.data
  if (data.totals.total === 0) {
    return (
      <>
        {head}
        <AwaitingAcceptance />
        <EmptyState title={t('pages.dashboard.emptyTitle')} body={t('pages.dashboard.emptyBody')} />
      </>
    )
  }

  const kpis = [
    { key: 'kpiTotal', value: data.totals.total },
    { key: 'kpiOpen', value: data.totals.open },
    { key: 'kpiDone', value: data.totals.completed },
    { key: 'kpiOverdue', value: data.totals.overdueDeadlines },
    { key: 'kpiRate', value: `${data.totals.completionRate} %` },
  ] as const

  return (
    <>
      {head}
      <p className="mb-4 text-sm text-muted-foreground">{t('dashboard.readOnlyNote')}</p>
      <AwaitingAcceptance />

      <ul
        className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-5"
        aria-label={t('pages.dashboard.title')}
      >
        {kpis.map((kpi) => (
          <li key={kpi.key} className="rounded-lg border bg-card p-4" data-kpi={kpi.key}>
            <div className="text-3xl font-semibold">{kpi.value}</div>
            <div className="text-sm text-muted-foreground">{t(`dashboard.${kpi.key}`)}</div>
          </li>
        ))}
      </ul>

      <div className="grid gap-6 lg:grid-cols-2">
        <BarChartCard
          title={t('dashboard.statusChartTitle')}
          valueLabel={t('dashboard.colCount')}
          data={data.byStage.map((s) => ({
            label: t(`dashboard.stage.${s.stage}`),
            value: s.count,
          }))}
        />
        <BarChartCard
          title={t('dashboard.typeChartTitle')}
          valueLabel={t('dashboard.colCount')}
          data={data.byOrderType.map((s) => ({
            label: s.orderType ?? t('dashboard.noType'),
            value: s.count,
          }))}
        />
        <BarChartCard
          title={t('dashboard.funnelTitle')}
          valueLabel={t('dashboard.colReached')}
          colorByBar={false}
          data={data.funnel.map((s) => ({
            label: t(`dashboard.stage.${s.stage}`),
            value: s.reached,
          }))}
        />

        <section className="rounded-lg border bg-card p-4" aria-labelledby="dash-upcoming">
          <h2 id="dash-upcoming" className="mb-2 text-lg font-medium">
            {t('dashboard.upcomingTitle')}
          </h2>
          {data.upcomingDeadlines.length === 0 ? (
            <p className="text-muted-foreground">{t('dashboard.upcomingNone')}</p>
          ) : (
            <ul className="divide-y" data-testid="upcoming">
              {data.upcomingDeadlines.map((item) => (
                <li
                  key={`${item.orderId}-${item.typeCode}-${item.actionDueAt}`}
                  className="flex flex-wrap items-center justify-between gap-2 py-2"
                >
                  <div>
                    <Link
                      to="/orders/$orderId"
                      params={{ orderId: item.orderId }}
                      className="inline-flex min-h-11 items-center font-medium text-primary underline"
                    >
                      {item.orderNumber}
                    </Link>
                    <p className="text-sm text-muted-foreground">
                      {kindLabel(t, item.typeCode)}
                      {item.address ? ` · ${item.address}` : ''}
                    </p>
                  </div>
                  <div className="text-right text-sm">
                    <AmpelBadge ampel={toAmpel(item.ampel)} />
                    <div className="text-muted-foreground">{formatDate(item.actionDueAt)}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="mt-6 rounded-lg border bg-card p-4" aria-labelledby="dash-doc">
        <h2 id="dash-doc" className="mb-2 text-lg font-medium">
          {t('dashboard.docTitle')}
        </h2>
        {data.documentation.length === 0 ? (
          <p className="text-muted-foreground">{t('dashboard.docNone')}</p>
        ) : (
          <ul className="grid gap-3 md:grid-cols-2" data-testid="doc-progress">
            {data.documentation.map((item) => {
              const labelId = `doc-${item.orderId}`
              return (
                <li key={item.orderId}>
                  <div className="flex justify-between gap-2 text-sm">
                    <span id={labelId} className="font-medium">
                      {item.orderNumber}
                    </span>
                    <span className="text-muted-foreground">
                      {t('dashboard.docProgress', { done: item.stepsDone, total: item.stepsTotal })}
                    </span>
                  </div>
                  <progress
                    aria-labelledby={labelId}
                    value={item.stepsDone}
                    max={Math.max(item.stepsTotal, 1)}
                    className="h-3 w-full overflow-hidden rounded-full [&::-moz-progress-bar]:bg-primary [&::-webkit-progress-bar]:bg-muted [&::-webkit-progress-value]:bg-primary"
                  />
                </li>
              )
            })}
          </ul>
        )}
      </section>
    </>
  )
}
